import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';

import { useAISwitchStore } from '~/common/mui/Switch/SwitchAI/aiSwitchStore';

import QuestionLibraryBuilder from './QuestionLibraryBuilder';
import QuestionLibraryProvidersComponent from './layouts/QuestionLibraryProvidersComponent';
import {
  useQuestionLibraryActions,
  useQuestionLibraryStore,
} from './store/questionLibraryStore';

const collectCanHideQuestionNames = (screenData: Record<string, unknown>) => {
  const canHideQuestionNames = new Set<string>();
  const questionAnswers = screenData.questionAnswers;

  if (
    !questionAnswers ||
    typeof questionAnswers !== 'object' ||
    Array.isArray(questionAnswers)
  ) {
    return [];
  }

  Object.entries(questionAnswers).forEach(([questionName, questionAnswer]) => {
    if (!questionAnswer || typeof questionAnswer !== 'object') {
      return;
    }

    if ('canHide' in questionAnswer && questionAnswer.canHide === true) {
      canHideQuestionNames.add(questionName);
    }
  });

  return [...canHideQuestionNames];
};

function AISwitchCanHideBridge({ enabled }: Readonly<{ enabled: boolean }>) {
  const shouldSyncAiHiddenState = enabled;
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenData } = useQuestionLibraryActions();
  const isAISwitchOn = useAISwitchStore((s) => s.isAISwitchOn);

  // derive the full "is hidden" map from current screenData + AI switch value.
  const nextAiHiddenByQuestion = useMemo(() => {
    if (!shouldSyncAiHiddenState) {
      return screenData.isAiHiddenByQuestion ?? {};
    }

    const canHideQuestionNames = collectCanHideQuestionNames(screenData);
    return canHideQuestionNames.reduce((acc, questionName) => {
      acc[questionName] = !isAISwitchOn;
      return acc;
    }, {});
  }, [shouldSyncAiHiddenState, screenData, isAISwitchOn]);

  // persist only when derived values differ from what is already in the store.
  // questionLibrary utils reroutes .isAiHidden lookups to screenData.isAiHiddenByQuestion
  useEffect(() => {
    if (!shouldSyncAiHiddenState) return;

    if (
      screenData.isAISwitchOn === isAISwitchOn &&
      isEqual(screenData.isAiHiddenByQuestion, nextAiHiddenByQuestion)
    ) {
      return;
    }

    updateScreenData({
      isAISwitchOn,
      isAiHiddenByQuestion: nextAiHiddenByQuestion,
    });
  }, [
    shouldSyncAiHiddenState,
    isAISwitchOn,
    nextAiHiddenByQuestion,
    screenData,
    updateScreenData,
  ]);

  return null;
}

type QuestionLibraryProps = {
  questions?: any[];
  screenData?: any;
  sideEffects?: React.ComponentType[];
  formMethods?: any;
  shouldUpdateScreenData?: boolean;
  isAiSwitchButtonEnabled?: boolean;
};

export default function QuestionLibrary({
  questions,
  screenData,
  sideEffects = [],
  formMethods,
  shouldUpdateScreenData,
  isAiSwitchButtonEnabled = false,
}: Readonly<QuestionLibraryProps>) {
  return (
    <QuestionLibraryProvidersComponent
      methods={formMethods}
      screenData={screenData}
      shouldUpdateScreenData={shouldUpdateScreenData}
    >
      <AISwitchCanHideBridge enabled={isAiSwitchButtonEnabled} />
      <QuestionLibraryBuilder questions={questions} />
      {sideEffects.map((SideEffect, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <SideEffect key={i} />
      ))}
    </QuestionLibraryProvidersComponent>
  );
}
